import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Trash, MagnifyingGlass, Warning, Lock, SignOut } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import TeenVestLoader from '../components/TeenVestLoader';

const ADMIN_KEY_STORAGE = 'teenvest_admin_key';

const AdminLogin = ({ onAuthed }) => {
  const [key, setKey] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/admin/login', { admin_key: key });
      sessionStorage.setItem(ADMIN_KEY_STORAGE, key);
      toast.success('Admin authenticated');
      onAuthed(key);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Wrong password';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen noise-bg flex items-center justify-center px-4" data-testid="admin-login">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="glass-card p-8">
          <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 mb-4">
            <Lock size={28} weight="fill" className="text-black" />
          </div>
          <h1 className="text-2xl font-unbounded font-bold text-center text-white mb-1">Admin Panel</h1>
          <p className="text-sm text-zinc-400 text-center mb-6">Enter the admin password to continue.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <ShieldCheck size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Admin password"
                className="pl-10 bg-black/50 border-white/10 text-white"
                autoFocus
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || !key.trim()}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold py-5 rounded-full"
              data-testid="admin-login-submit"
            >
              {submitting ? 'Verifying...' : 'Unlock Admin'}
            </Button>
          </form>
          <p className="text-[11px] text-zinc-500 text-center mt-4">
            Set the password in <span className="text-zinc-400 font-mono">backend/.env → ADMIN_KEY</span>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

const AdminPanel = ({ adminKey, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);

  const headers = { 'X-Admin-Key': adminKey };

  const fetchUsers = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/admin/users', { headers });
      setUsers(res.data.users || []);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Unknown error';
      setError(msg);
      // If invalid, clear stored key
      if (err.response?.status === 401) {
        sessionStorage.removeItem(ADMIN_KEY_STORAGE);
        onLogout();
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, [adminKey]);

  const handleDelete = async (username) => {
    if (!window.confirm(`Permanently delete "${username}"? This cannot be undone.`)) return;
    setDeleting(username);
    try {
      await api.delete(`/admin/delete-user/${username}`, { headers });
      setUsers(prev => prev.filter(u => u.username !== username));
      toast.success(`"${username}" deleted`);
    } catch {
      toast.error(`Failed to delete "${username}"`);
    } finally { setDeleting(null); }
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return u.username.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="min-h-screen noise-bg flex items-center justify-center">
        <TeenVestLoader size={104} label="Loading users..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen noise-bg flex items-center justify-center px-4">
        <div className="glass-card p-8 max-w-md text-center">
          <Warning size={48} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Error Loading Users</h2>
          <p className="text-zinc-400 mb-4">{error}</p>
          <Button onClick={fetchUsers} className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen noise-bg" data-testid="admin-panel">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck size={32} weight="fill" className="text-gold" />
              <h1 className="text-3xl font-unbounded font-bold text-white">User Management</h1>
            </div>
            <p className="text-zinc-400">{users.length} registered users</p>
          </div>
          <Button
            variant="ghost"
            onClick={onLogout}
            className="text-zinc-400 hover:text-white"
            data-testid="admin-logout"
          >
            <SignOut size={16} className="mr-2" /> Lock
          </Button>
        </motion.div>

        <div className="relative max-w-sm mb-6">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-black/50 border-white/10 text-white"
            data-testid="admin-search"
          />
        </div>

        <div className="glass-card overflow-hidden">
          <div className="grid grid-cols-[1fr_1.5fr_0.8fr_0.6fr_0.6fr_auto] gap-4 p-4 border-b border-white/10 text-xs font-bold text-zinc-400 uppercase tracking-wider">
            <span>Username</span><span>Email</span><span>Balance</span><span>Stocks</span><span>Traded</span><span></span>
          </div>

          <AnimatePresence>
            {filtered.map((user, i) => (
              <motion.div
                key={user.username}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, height: 0 }}
                transition={{ delay: i * 0.02 }}
                className="grid grid-cols-[1fr_1.5fr_0.8fr_0.6fr_0.6fr_auto] gap-4 p-4 border-b border-white/5 items-center hover:bg-white/5 transition-colors"
                data-testid={`admin-user-row-${user.username}`}
              >
                <span className="text-white font-medium truncate">{user.username}</span>
                <span className="text-zinc-400 text-sm truncate">{user.email}</span>
                <span className="text-gold font-mono text-sm">${(user.balance || 0).toLocaleString()}</span>
                <span className="text-zinc-300 text-sm">{user.stocks_held}</span>
                <span className={`text-xs font-bold ${user.has_traded ? 'text-emerald-400' : 'text-red-400'}`}>{user.has_traded ? 'Yes' : 'No'}</span>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => handleDelete(user.username)}
                  disabled={deleting === user.username}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid={`admin-delete-${user.username}`}
                >
                  {deleting === user.username ? (
                    <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                  ) : (<Trash size={18} />)}
                </Button>
              </motion.div>
            ))}
          </AnimatePresence>

          {filtered.length === 0 && (<div className="p-8 text-center text-zinc-500">No users found</div>)}
        </div>

        <div className="mt-4 flex items-center gap-2 text-xs text-zinc-600">
          <Warning size={14} />
          <span>Deleting a user permanently removes their account, portfolio, and transactions.</span>
        </div>
      </div>
    </div>
  );
};

const Admin = () => {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(ADMIN_KEY_STORAGE) || '');

  if (!adminKey) {
    return <AdminLogin onAuthed={(key) => setAdminKey(key)} />;
  }
  return (
    <AdminPanel
      adminKey={adminKey}
      onLogout={() => {
        sessionStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey('');
      }}
    />
  );
};

export default Admin;
